import { AnimationController } from "@/core/battle/AnimationController";
import { ControlStateMachine } from "@/core/battle/ControlStateMachine";
import { EntityRegistry } from "@/core/battle/EntityRegistry";
import { PathCalculator } from "@/core/battle/PathCalculator";
import { SoundManager } from "@/core/battle/SoundManager";
import { TurnManager } from "@/core/battle/TurnManager";
import { ControlState, FactionType, PathInfo, TerrainType } from "@/core/types/battle.types";
import { smoothPath } from "@/core/utils/battle.utils";
import { coordsToId } from "@/core/utils/hex.utils";
import { BattleBus } from "@/events/Events";
import { BattleMut } from "@/store/battleMutations";
import { useBattleStore } from "@/store/useBattleStore";
import { PathRenderer } from "@/view/renderers/PathRenderer";
import { UnitStack } from "@/view/renderers/UnitStack";

/**
 * Executes movement actions
 * Handles pathfinding, animation, state updates
 */
export class MovementExecutor {
    private currentAbortController: AbortController | null = null;
    
    constructor(
    private animationController: AnimationController,
        private pathRendeder: PathRenderer,
        private entityRegistry: EntityRegistry,
    ) {}
    
    /**
     * Execute unit movement
     * Handles state transitions, animations, and cleanup
     */
    async execute(unitId: string, pathInfo: PathInfo, isOpportunityStrike?: boolean): Promise<void> {
        // Cancel any previous movement
        // this.cancelCurrentMovement();
        
        // Create abort controller for this movement
        this.currentAbortController = new AbortController();
        const signal = this.currentAbortController.signal;
        
        const state = useBattleStore.getState();
        const unit = state.units.get(unitId);
        const unitVisual = this.entityRegistry.getUnit(unitId);

        if (!unit || !unitVisual) {
            console.warn('[MovementExecutor] Unit or visual not found');
            return;
        }
        
        
        const startHexId = coordsToId(unit);

        
        // Validate movement cost
        // if (unit.currentStamina < pathInfo.cost) {
        //     console.warn('[MovementExecutor] Not enough stamina');
        //     return;
        // }

        if (!pathInfo.lastStep) {
            console.warn('[MovementExecutor] No valid path found');
            return;
        }

        
        
        try {
            BattleBus.emit('STAMINA_DRAIN_START', { unitId, amount: pathInfo.cost });

            // Якщо шлях є — анімуємо, якщо немає — пропускаємо await
            if (pathInfo.path.length > 0) {
                SoundManager.playMovementSound();
                const splinePoints = this.prepareForMovement(unitVisual, pathInfo.path, pathInfo.lastStep.terrain === TerrainType.Hill);
                await this.animationController.playMovement(unitVisual, splinePoints, { signal });
            }

            if (!signal.aborted) {
                SoundManager.stopMovementSound();
                this.updateUnitPosition(unitVisual, pathInfo.lastStep.id, pathInfo.cost);
            }
        } catch (err: any) {
            if (signal.aborted || err.message?.includes('aborted')) {
                console.log('[MovementExecutor] Movement cancelled');
            } else {
                console.error('[MovementExecutor] Movement failed:', err);
            }
        } finally {
            // 7. Cleanup
            this.pathRendeder.clear();
            this.currentAbortController = null;
            SoundManager.stopMovementSound();
        }
    }
    
    /**
     * Cancel current movement animation
     */
    cancelCurrentMovement(): void {
        if (this.currentAbortController) {
            this.currentAbortController.abort();
            this.currentAbortController = null;
        }
    }
    
    private prepareForMovement(unit: UnitStack, path: string[], isHill: boolean = false): {x: number, y: number}[]  {
        this.pathRendeder.clear();
        const fullPath = PathCalculator.constructFullPath(
            unit, 
            path,
            this.entityRegistry.getAllHexes()
        );
        return smoothPath(fullPath, isHill);
    }
    
    
    private updateUnitPosition(
        unitVisual: UnitStack,
        targetHexId: string,
        staminaCost: number
    ): void {
        const hex = this.entityRegistry.getHex(targetHexId);
        const unit = useBattleStore.getState().units.get(unitVisual.unitId);

        if (!hex || !unit) return;
        
        // Update visual
        unitVisual.q = hex.data.q;
        unitVisual.r = hex.data.r;

        const oldHexId = coordsToId(unit); // unit coords before update is startHexId
        const oldHex = this.entityRegistry.getHex(oldHexId); // unit coords before update is startHexId

        oldHex?.vacate();
        hex.occupy(unit.unitId);

        const currentStamina = unit.currentStamina - staminaCost

        BattleBus.emit('STAMINA_DRAIN_STOP', {
            unitId: unit.unitId,
            amount: currentStamina
        });

        useBattleStore.getState().apply((state) => {
            BattleMut.updateHex(state, 
                oldHexId, { entityId: undefined }
            );
            BattleMut.updateHex(state, 
                targetHexId, { entityId: unit.unitId }
            );
            BattleMut.resetSelectedPath(state);
            BattleMut.updateUnit(state, unit.unitId, {
                q: hex.data.q,
                r: hex.data.r,
                currentStamina: currentStamina
            });
            
        });



        
        // const resPath = PathCalculator.getReachableNodes(
        //     unitVisual, 
        //     { currentStamina: currentStamina, speed: unit.stats.speed },
        //     useBattleStore.getState().battleMap
        // )
        // useBattleStore.getState().setReachableHexes(resPath.reachableIds, resPath.reachableMap);

    }

}